import { useState, type FC, useEffect, Suspense, useRef, useCallback } from 'react';

import { ErrorBoundary } from '@packages/shared/src/components/ErrorBoundary/ErrorBoundary';
import { usePushQueryChange } from '@packages/shared/src/hooks/usePushQueryChange/usePushQueryChange';
import type { GTMEventGlycerinPagination } from '@packages/tracking/src/types/events';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { InfiniteProductGridFragmentFragmentDoc } from '@packages/gql/generated/shopping/InfiniteProductGridFragmentFragmentDoc';
import type { Device } from '@packages/themes/src/default';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useAbTesting } from '@packages/shared/src/abtesting/useAbTesting/useAbTesting';

import { usePaging } from '../../hooks/usePaging';
import { InfiniteProductGridPageLoaderSwitch } from './InfiniteProductGrid.PageLoaderSwitch';
import { ProductListVirtualPageSkeleton } from '../ProductList/ProductList.VirtualPageSkeleton';
import { useDisplayMode } from '../../hooks/useDisplayMode';
import { InfiniteLoadingFooter } from './InfiniteLoadingFooter';
import { encodePage } from '../../queryEncoding';
import { InfiniteLoadingHeader } from './InfiniteLoadingHeader';
import { getTestId } from '../../utils/featureFlags/getTestId';
import { fewerProductsPerPage } from '../../activeFeatureFlags';
import type { ProductListVirtualPageProps } from '../ProductList/ProductList.VirtualPage.client';
import { ProductListVirtualPageMemoized } from '../ProductList/ProductList.VirtualPage.memoized';

/* GraphQL */ `
  fragment InfiniteProductGridFragment on SearchProductResult {
    ...ProductGridLoaderSwitchFragment
    ...ProductListVirtualPageFragment
    ...UsePagingFragment
    ...UseDisplayModeFragment

    summary {
      totalResultCount
    }
  }
`;

export type InfiniteProductGridProps = Pick<
  ProductListVirtualPageProps,
  'onProductFirstBecameVisible'
> & {
  maskedResult: FragmentType<typeof InfiniteProductGridFragmentFragmentDoc>;
  device: Device;
  referrer?: string;
  /**
   * How many products are loaded at once on-demand when scrolling down
   *
   * @default 72 or whatever is defined in query param for (initial) page size */
  additionalPageSize?: number;
  VirtualPageComponent?: FC<ProductListVirtualPageProps>;
};

// TODO consider unloading pages from memory ("de-rendering" with memoized size, etc.) when they are not visible anymore, for performance
// TODO consider using a library like react-window or react-virtualized to handle the infinite scrolling and virtualization
const SKIP_PREVIOUS_PAGES_ON_FIRST_RENDER = true;

export const InfiniteProductGrid: FC<InfiniteProductGridProps> = (props) => {
  const {
    maskedResult,
    device,
    referrer,
    additionalPageSize: additionalPageSizeOverride,
    VirtualPageComponent = ProductListVirtualPageMemoized,
    onProductFirstBecameVisible,
  } = props;

  const {
    search: { paginationMode },
  } = useConfig();

  const result = unmask<typeof InfiniteProductGridFragmentFragmentDoc>(maskedResult);
  const { currentPage, pageSize: initialPageSize } = usePaging(result);
  const additionalPageSize = additionalPageSizeOverride ?? initialPageSize;
  const { displayMode } = useDisplayMode(result);

  // TODO figure out how to load the correct page, so that the remembered page data (scroll to sku) still works

  const initialPage = useRef(currentPage);

  // both one-based, not an index
  const [firstPage, setFirstPage] = useState(
    SKIP_PREVIOUS_PAGES_ON_FIRST_RENDER ? initialPage.current : 1,
  ); // NOTE currently unused, as the feature to scroll back to the previous position is not yet implemented and will be pushed to a follow-up ticket
  const [lastPage, setLastPage] = useState(initialPage.current);

  const dispatchGtmEvent = useTracking();

  const { setOutcome } = useAbTesting();

  useEffect(() => {
    // initial page is not tracked because it is not a pagination interaction
    if (lastPage <= 1) return;

    dispatchGtmEvent<GTMEventGlycerinPagination>({
      event: 'Pagination',
      PaginationData: {
        pageNumber: lastPage,
      },
    });
  }, [dispatchGtmEvent, lastPage]);

  const maybeShowNextPageBasedOnSeenProducts = useCallback(
    ({ pageNumber, indexInPage = 0 }: { pageNumber: number; indexInPage?: number }) => {
      const pageSize = pageNumber <= 1 ? initialPageSize : additionalPageSize;
      // load next page when 70% of the current page is visible
      if (indexInPage + 1 >= 0.7 * pageSize) {
        setLastPage((previous) => {
          // page index is 0-based, +1 gives corresponding 1-based page number, +2 gives next page number
          const nextPageNumber = pageNumber + 1;

          // only change the number of pages to display if the next page number is greater than the current number of pages
          // otherwise it would be possible to hide pages again due to race conditions
          // shown pages should always be monotonically increasing
          return Math.max(previous, nextPageNumber);
        });

        if (lastPage < pageNumber) {
          setOutcome(getTestId(fewerProductsPerPage), { LOAD: pageNumber });
        }
      }
    },
    [additionalPageSize, initialPageSize, lastPage, setOutcome],
  );

  const handleProductFirstBecameVisible = useCallback(
    ({ pageNumber, indexInPage }: { indexInPage?: number; pageNumber: number }) => {
      onProductFirstBecameVisible?.({ pageNumber, indexInPage });

      // new version of infinite scrolling uses manual loading instead of view-based auto-loading
      if (paginationMode === 'loadMore') return;

      maybeShowNextPageBasedOnSeenProducts({
        pageNumber,
        indexInPage,
      });
    },
    [maybeShowNextPageBasedOnSeenProducts, onProductFirstBecameVisible, paginationMode],
  );

  const { pushQueryChange } = usePushQueryChange({ trailingSlash: true });

  // sync page to URL when loading new pages
  useEffect(() => {
    pushQueryChange(encodePage(lastPage > 1 ? lastPage : undefined), { shallow: true });
  }, [pushQueryChange, lastPage]);

  // TODO find out why loading new page leads to infinite image requests in network tab, always same images again and again, happens only with devtools open and not when the profiler is running

  // currently gaps are not supported, so all pages between firstPage and lastPage are visible
  const visiblePages = Array.from({ length: lastPage - firstPage + 1 }).map(
    (_, pageIndex) => pageIndex + firstPage,
  );

  return (
    <>
      {SKIP_PREVIOUS_PAGES_ON_FIRST_RENDER && firstPage > 1 && (
        <InfiniteLoadingHeader
          onLoadMore={() => {
            setFirstPage((previous) => Math.max(1, previous - 1));
          }}
        />
      )}

      {visiblePages.map((pageNumber) =>
        // the initial page should be available in the server-filled cache, and does not need to be lazy-loaded
        pageNumber === initialPage.current && initialPage.current === 1 ? (
          <VirtualPageComponent
            // NOTE: there is no other possible key for the page than the page index
            // eslint-disable-next-line react/no-array-index-key
            key={`page-${pageNumber}`}
            maskedResult={result}
            onProductFirstBecameVisible={handleProductFirstBecameVisible}
            pageNumber={pageNumber}
          />
        ) : (
          <ErrorBoundary key={`page-${pageNumber}`}>
            <Suspense
              fallback={
                <ProductListVirtualPageSkeleton
                  displayMode={displayMode ?? 'cards'}
                  count={additionalPageSize}
                />
              }
            >
              <InfiniteProductGridPageLoaderSwitch
                limit={additionalPageSize}
                offset={initialPageSize + (pageNumber - 2) * additionalPageSize} // count all previous dynamic pages, excluding this one; dynamic pages start at page number 2
                device={device}
                referrer={referrer}
                VirtualPageComponent={VirtualPageComponent}
                maskedResult={result}
                // ProductGridProps
                onProductFirstBecameVisible={handleProductFirstBecameVisible}
                pageNumber={pageNumber}
              />
            </Suspense>
          </ErrorBoundary>
        ),
      )}

      {paginationMode === 'loadMore' && (
        <InfiniteLoadingFooter
          seenProductCount={Math.min(
            result.summary.totalResultCount,
            initialPageSize + (lastPage - 1) * additionalPageSize,
          )}
          totalProductCount={result.summary.totalResultCount}
          onLoadMore={() => {
            setLastPage((previous) => previous + 1);

            setOutcome(getTestId(fewerProductsPerPage), { LOAD: lastPage + 1 });
          }}
        />
      )}
    </>
  );
};
